import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { getProductBadge, getProductCategory, getProductLabel } from '@socialgoodsoftware/frbac';
import { getCategoryColor, getCategoryLabel } from './category-color';
import { productPanelId } from './product-switcher-ids';
import { useProductSwitcher } from './product-switcher-context';

/**
 * The other half of the context bar: where you are, and the way to somewhere else. It opens
 * the menu on the products, so the bar beside it searches products and nothing else.
 */
export function ContextProduct() {
  const switcher = useProductSwitcher();

  if (switcher === null || switcher.product.length === 0) {
    return null;
  }

  const isOpen = switcher.isPanelOpen;
  const label = getProductLabel(switcher.product);
  const category = getProductCategory(switcher.product);

  // Open on the other list, this half is the way across to the products rather than the way out,
  // so there is a path in both directions between the two lists. Only its own list closes it —
  // and the chevron is shown for exactly the states where it does, since that is what it claims.
  const isToggle = !isOpen || switcher.mode === 'products';

  function handleClick() {
    if (isToggle && switcher !== null) {
      switcher.togglePanel('products');
    } else if (switcher !== null) {
      switcher.openPanel('products');
    }
  }

  return (
    <ButtonBase
      ref={switcher.triggerRef}
      onClick={handleClick}
      aria-expanded={isOpen}
      aria-controls={productPanelId}
      aria-label={`${label} — open the product menu`}
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'stretch',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, flexShrink: 0 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            bgcolor: getCategoryColor(category),
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {getProductBadge(switcher.product)}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
          <Typography
            data-product-category
            sx={{ fontSize: '10px', color: 'text.secondary', lineHeight: 1.2, letterSpacing: '0.03em' }}
          >
            {getCategoryLabel(category)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
            {label}
          </Typography>
        </Box>
        <ExpandMoreRoundedIcon
          aria-hidden="true"
          sx={{
            fontSize: 20,
            color: 'text.secondary',
            flexShrink: 0,
            // Held in the layout rather than unmounted, so crossing lists never jogs the bar.
            visibility: isToggle ? 'visible' : 'hidden',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      </Box>
    </ButtonBase>
  );
}
